import type { ReactNode } from 'react'
import { Radio, RadioGroup } from '@structural-climbing/ui'

export interface OptionCard<T extends string> {
  value: T
  title: string
  hint?: string
  isDisabled?: boolean
  /** Shown inside the card only while it is selected (a fee field, say). */
  children?: ReactNode
}

/**
 * A choice between a few things that each need a sentence of explanation.
 * One bordered card per option; the selected one takes the brand colour, and
 * any fields that belong to it open inside the same card.
 *
 * A HeroUI RadioGroup underneath, so it is one tab stop with arrow keys,
 * like any radio group.
 */
export function OptionCards<T extends string>({
  label,
  value,
  onChange,
  options,
  columns = 1,
}: {
  label: string
  value: T
  onChange: (value: T) => void
  options: OptionCard<T>[]
  columns?: 1 | 2
}) {
  return (
    <RadioGroup
      label={label}
      value={value}
      onValueChange={(v) => onChange(v as T)}
      classNames={{
        label: 'text-sm font-semibold text-foreground',
        wrapper: `grid gap-2.5 ${columns === 2 ? 'sm:grid-cols-2' : ''}`,
      }}
    >
      {options.map((option) => {
        const selected = value === option.value
        return (
          <div
            key={option.value}
            className={`flex flex-col rounded-xl ${
              option.isDisabled
                ? 'border-1.5 border-dashed border-default-300 bg-content2'
                : selected
                  ? 'border-2 border-primary bg-primary-50'
                  : 'border-1.5 border-default-300 bg-content1'
            }`}
          >
            <Radio
              value={option.value}
              isDisabled={option.isDisabled}
              classNames={{
                base: 'm-0 flex w-full max-w-none items-start gap-3 rounded-xl p-4',
                wrapper: 'mt-0.5 shrink-0',
                labelWrapper: 'ms-0 flex-1',
              }}
            >
              <span className="flex flex-col gap-0.5">
                <span className="text-[15px] font-semibold text-foreground">{option.title}</span>
                {option.hint && <span className="text-sm text-foreground/70">{option.hint}</span>}
              </span>
            </Radio>
            {/* Inside the card but outside the radio's label: a field inside the
                label would have every click on it re-select the radio. */}
            {selected && option.children && (
              <div className="px-4 pb-4 sm:ps-12">{option.children}</div>
            )}
          </div>
        )
      })}
    </RadioGroup>
  )
}
